import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';

import { createFallbackSceneDocuments } from '../../game/sceneLoader';
import {
  getConversationCount,
  getUserProfile,
  updateUserLastSeen,
  upsertUserProfile,
} from '../../services/firebase/firestore';
import { deleteOwnedScene, listGalleryScenes } from '../../services/firebase/scenes';
import { useGameStore } from '../../stores/gameStore';
import type { SceneDocument } from '../../types/scene';
import { DEFAULT_SPRITE_COLORS, type AvatarFormValues, type UserProfile } from '../../types/user';
import { isUserCharacterDefined } from '../../lib/userCharacterProfile';
import { mergeGalleryScenes } from '../../app/appShared';
import { useNpcCatalog } from '../../app/hooks/useNpcCatalog';
import { sortScenesByRecency } from '../../services/firebase/scenePersistenceHelpers';
import type { AuthOutletContext } from '../components/AuthGuard';
import { ConnectionBanner } from '../components/ConnectionBanner';
import { AppContextProvider, type AppContextValue } from './AppContext';

const GALLERY_SYNC_WARNING =
  'A recently saved scene has not synced to the gallery yet. Showing the local copy.';

const createAnonymousGuestProfile = (isApproved: boolean, isAdmin: boolean): UserProfile => ({
  displayName: 'Explorer',
  bio: '',
  hasCompletedCharacterSetup: true,
  spriteId: 'hero_01',
  spriteColors: DEFAULT_SPRITE_COLORS,
  isAnonymous: true,
  isApproved,
  role: isAdmin ? 'admin' : 'user',
});

const mergeLoadedScenesWithOptimistic = (
  loadedScenes: SceneDocument[],
  optimisticScenes: SceneDocument[],
): SceneDocument[] => {
  const byId = new Map<string, SceneDocument>();
  loadedScenes.forEach((scene) => byId.set(scene.id, scene));
  optimisticScenes.forEach((scene) => {
    if (!byId.has(scene.id)) {
      byId.set(scene.id, scene);
    }
  });
  return sortScenesByRecency([...byId.values()]);
};

export function AppLayout(): JSX.Element {
  const { authUser, authClaims, isAuthBypass } = useOutletContext<AuthOutletContext>();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [conversationCount, setConversationCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showAvatarEditor, setShowAvatarEditor] = useState(false);
  const [galleryScenes, setGalleryScenes] = useState<SceneDocument[]>(() =>
    createFallbackSceneDocuments(),
  );
  const [optimisticGalleryScenes, setOptimisticGalleryScenes] = useState<SceneDocument[]>([]);
  const optimisticGalleryScenesRef = useRef<SceneDocument[]>([]);
  const [isGalleryLoading, setIsGalleryLoading] = useState(false);
  const [sceneGalleryError, setSceneGalleryError] = useState<string | null>(null);

  const setPlayerSpriteColors = useGameStore((s) => s.setPlayerSpriteColors);
  const setPlayerAvatarLayers = useGameStore((s) => s.setPlayerAvatarLayers);
  const setPlayerAvatarMode = useGameStore((s) => s.setPlayerAvatarMode);
  const setPlayerCustomAvatarId = useGameStore((s) => s.setPlayerCustomAvatarId);
  const setNpcConfigs = useGameStore((s) => s.setNpcConfigs);
  const npcConfigs = useGameStore((s) => s.npcConfigs);

  const { applyOptimisticUserNpcToScene, refreshNpcConfigs } = useNpcCatalog({
    isAuthBypass,
    setNpcConfigs,
  });

  useEffect(() => {
    optimisticGalleryScenesRef.current = optimisticGalleryScenes;
  }, [optimisticGalleryScenes]);

  useEffect(() => {
    const load = async (): Promise<void> => {
      if (isAuthBypass) {
        const bypassProfile: UserProfile = {
          displayName: 'Local Explorer',
          bio: 'Local bypass mode',
          hasCompletedCharacterSetup: true,
          spriteId: 'hero_01',
          spriteColors: DEFAULT_SPRITE_COLORS,
          isAnonymous: true,
          isApproved: true,
          role: 'admin',
        };
        setProfile(bypassProfile);
        setConversationCount(0);
        setPlayerSpriteColors(bypassProfile.spriteColors);
        setPlayerAvatarLayers(bypassProfile.avatarLayers ?? null);
        setPlayerAvatarMode(bypassProfile.avatarMode ?? 'layers');
        setPlayerCustomAvatarId(bypassProfile.customAvatarId ?? null);
        setShowAvatarEditor(false);
        await refreshNpcConfigs(null, null);
        setIsLoading(false);
        return;
      }

      if (!authUser) {
        setProfile(null);
        setConversationCount(0);
        setPlayerAvatarLayers(null);
        setPlayerAvatarMode('layers');
        setPlayerCustomAvatarId(null);
        setShowAvatarEditor(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const conversationCountPromise = (async (): Promise<number> => {
        try {
          const loaded = await Promise.resolve(getConversationCount(authUser.uid));
          return typeof loaded === 'number' ? loaded : 0;
        } catch {
          return 0;
        }
      })();
      const [existingProfile, totalConversations] = await Promise.all([
        getUserProfile(authUser.uid),
        conversationCountPromise,
      ]);

      if (existingProfile) {
        const resolvedProfile: UserProfile = {
          ...existingProfile,
          isApproved: authClaims.approved,
          role: authClaims.admin ? 'admin' : existingProfile.role,
        };
        setProfile(resolvedProfile);
        setPlayerSpriteColors(resolvedProfile.spriteColors);
        setPlayerAvatarLayers(resolvedProfile.avatarLayers ?? null);
        setPlayerAvatarMode(resolvedProfile.avatarMode ?? 'layers');
        setPlayerCustomAvatarId(resolvedProfile.customAvatarId ?? null);
        setShowAvatarEditor(!isUserCharacterDefined(resolvedProfile));
        await updateUserLastSeen(authUser.uid).catch(() => undefined);
      } else if (authUser.isAnonymous) {
        const guestProfile = createAnonymousGuestProfile(authClaims.approved, authClaims.admin);
        setProfile(guestProfile);
        setPlayerSpriteColors(guestProfile.spriteColors);
        setPlayerAvatarLayers(guestProfile.avatarLayers ?? null);
        setPlayerAvatarMode(guestProfile.avatarMode ?? 'layers');
        setPlayerCustomAvatarId(guestProfile.customAvatarId ?? null);
        setShowAvatarEditor(true);
      } else {
        setShowAvatarEditor(true);
      }

      setConversationCount(totalConversations);
      await refreshNpcConfigs(authUser.uid, null);
      setIsLoading(false);
    };

    void load();
  }, [
    authClaims.admin,
    authClaims.approved,
    authUser,
    isAuthBypass,
    refreshNpcConfigs,
    setPlayerAvatarLayers,
    setPlayerAvatarMode,
    setPlayerCustomAvatarId,
    setPlayerSpriteColors,
  ]);

  const refreshSceneGallery = useCallback(
    async (uid: string | null): Promise<void> => {
      const fallbackScenes = createFallbackSceneDocuments();
      if (isAuthBypass) {
        setOptimisticGalleryScenes([]);
        setGalleryScenes(fallbackScenes);
        setSceneGalleryError(null);
        return;
      }

      setIsGalleryLoading(true);
      setSceneGalleryError(null);
      try {
        const loadedScenes = await listGalleryScenes(uid ?? undefined);
        const currentOptimisticScenes = optimisticGalleryScenesRef.current;
        const loadedSceneIds = new Set(loadedScenes.map((scene) => scene.id));
        const remainingOptimisticScenes = currentOptimisticScenes.filter(
          (scene) => !loadedSceneIds.has(scene.id),
        );
        setOptimisticGalleryScenes(remainingOptimisticScenes);
        setGalleryScenes(
          mergeGalleryScenes(
            mergeLoadedScenesWithOptimistic(loadedScenes, remainingOptimisticScenes),
            fallbackScenes,
          ),
        );
        setSceneGalleryError(remainingOptimisticScenes.length > 0 ? GALLERY_SYNC_WARNING : null);
      } catch (error) {
        console.warn('[scenes] Failed to load gallery scenes; preserving current gallery.', error);
        setSceneGalleryError('Could not load scenes from Firestore. Preserving current gallery.');
      } finally {
        setIsGalleryLoading(false);
      }
    },
    [isAuthBypass],
  );

  const upsertGalleryScene = useCallback((scene: SceneDocument): void => {
    const fallbackScenes = createFallbackSceneDocuments();
    setSceneGalleryError(null);
    setOptimisticGalleryScenes((currentScenes) => {
      const nextScenes = currentScenes.filter((entry) => entry.id !== scene.id);
      nextScenes.push(scene);
      return sortScenesByRecency(nextScenes);
    });
    setGalleryScenes((currentScenes) => {
      const nextLoadedScenes = currentScenes.filter((entry) => entry.id !== scene.id);
      nextLoadedScenes.push(scene);
      return mergeGalleryScenes(nextLoadedScenes, fallbackScenes);
    });
  }, []);

  useEffect(() => {
    if (isAuthBypass) {
      void refreshSceneGallery(null);
      return;
    }
    if (!authUser) {
      setOptimisticGalleryScenes([]);
      setGalleryScenes(createFallbackSceneDocuments());
      return;
    }
    void refreshSceneGallery(authUser.uid);
  }, [authUser, isAuthBypass, refreshSceneGallery]);

  const availableSceneTemplates = useMemo(() => {
    const byId = new Map<string, SceneDocument>();
    createFallbackSceneDocuments().forEach((scene) => byId.set(scene.id, scene));
    galleryScenes.forEach((scene) => byId.set(scene.id, scene));
    return [...byId.values()];
  }, [galleryScenes]);

  const saveAvatar = useCallback(
    async (values: AvatarFormValues): Promise<void> => {
      if (isAuthBypass) {
        setProfile({
          ...values,
          isAnonymous: true,
          isApproved: true,
          role: 'admin',
        });
        setPlayerSpriteColors(values.spriteColors);
        setPlayerAvatarLayers(values.avatarLayers ?? null);
        setPlayerAvatarMode(values.avatarMode ?? 'layers');
        setPlayerCustomAvatarId(values.customAvatarId ?? null);
        setShowAvatarEditor(false);
        return;
      }

      if (!authUser) {
        return;
      }

      const nextProfile: UserProfile = {
        ...values,
        hasCompletedCharacterSetup: true,
        isAnonymous: authUser.isAnonymous,
        isApproved: authClaims.approved,
        role: authClaims.admin ? 'admin' : (profile?.role ?? 'user'),
      };
      await upsertUserProfile(authUser.uid, authUser.isAnonymous, values);
      setProfile(nextProfile);
      setPlayerSpriteColors(values.spriteColors);
      setPlayerAvatarLayers(values.avatarLayers ?? null);
      setPlayerAvatarMode(values.avatarMode ?? 'layers');
      setPlayerCustomAvatarId(values.customAvatarId ?? null);
      setShowAvatarEditor(false);
    },
    [
      authClaims.admin,
      authClaims.approved,
      authUser,
      isAuthBypass,
      profile,
      setPlayerAvatarLayers,
      setPlayerAvatarMode,
      setPlayerCustomAvatarId,
      setPlayerSpriteColors,
    ],
  );

  const deleteSceneFromGallery = useCallback(
    async (scene: SceneDocument): Promise<void> => {
      if (isAuthBypass || !authUser || scene.ownerId !== authUser.uid) {
        return;
      }
      const confirmed = window.confirm(`Delete "${scene.name}"? This cannot be undone.`);
      if (!confirmed) {
        return;
      }

      try {
        await deleteOwnedScene(authUser.uid, scene.id);
        setOptimisticGalleryScenes((currentScenes) =>
          currentScenes.filter((entry) => entry.id !== scene.id),
        );
        await refreshSceneGallery(authUser.uid);
      } catch (error) {
        console.error('Failed to delete scene:', error);
        window.alert('Failed to delete scene. Please try again.');
      }
    },
    [authUser, isAuthBypass, refreshSceneGallery],
  );

  if (isLoading) {
    return <div className="flex h-full items-center justify-center">Loading...</div>;
  }

  const contextValue: AppContextValue = {
    authUser,
    authClaims,
    isAuthBypass,
    profile,
    showAvatarEditor,
    galleryScenes,
    isGalleryLoading,
    sceneGalleryError,
    npcConfigs,
    conversationCount,
    availableSceneTemplates,
    refreshSceneGallery,
    refreshNpcConfigs,
    saveAvatar,
    upsertGalleryScene,
    setConversationCount,
    setProfile,
    applyOptimisticUserNpcToScene,
    deleteSceneFromGallery,
  };

  return (
    <AppContextProvider value={contextValue}>
      <ConnectionBanner />
      <Outlet />
    </AppContextProvider>
  );
}
